import React, { useState, useEffect, useRef, useMemo } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { useWorkbenchQueue } from "@/hooks/workbench/useWorkbench"
import { projectsInstance } from "@/services/ProjectsService"
import { SignalsActions } from "@/components/projects/blocks/SignalsActions"
import { SignalCards } from "@/components/projects/blocks/SignalCards"
import { useAuth } from "@/context/AuthContext"
import { useWorkspace } from "@/context/WorkspaceContext"
import { ScoreCard } from "@/components/projects/blocks/ScoreCard"
import { Button } from "@/components/ui/button"
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip"
import ReportDialog from "@/components/widgets/ReportDialog"
import { useReportsStore } from "@/store/store";
import NextThingCard from "@/components/projects/blocks/NextThingCard.tsx";
import { ScheduleFormDialog } from "@/components/settings/components/ScheduleFormDialog";
import EligibleReportDialog from "@/components/widgets/EligibleReportDialog";
import ReRunReportModal from "@/components/widgets/ReRunReportModal";
import ReputationCard from "@/components/vb/ReputationCard";
import { CompetitorFavicon, VbButton, VbEmpty, VbError, VbLoading } from "@/components/vb";

import { trackReportViewed } from "@/lib/posthog";
import {
    anyBuildingReportIdOf,
    buildReportPeriods,
    buildingReportIdOf,
    isBuildingStatus,
    normalizeDateString,
    type DashboardPeriodsView,
    type ReportPeriodSource,
    type ReportStatus,
} from "@/lib/reportPeriods";
import { MIN_GENERATION_DWELL_MS } from "@/lib/progressUtils";
import axios from "axios";

/** How often to re-read this company's report list while one is building.
 *  Matches the app-shell progress poll (useReports). */
const BUILDING_POLL_MS = 5000

/** Stable "not loaded yet" value, so the memo below doesn't hand the page a new
 *  empty list identity on every render. */
const EMPTY_PERIODS: DashboardPeriodsView = {
    periods: [],
    minStartDate: null,
    maxReportDate: null,
    defaultPeriod: null,
}

export default function ProjectsPage() {
    const navigate = useNavigate()
    const { projectId, reportId } = useParams<{ projectId: string; reportId: string }>()
    const [month, setMonth] = useState("")
    const [year, setYear] = useState("")
    const [report, setReport] = useState<any>(null)
    const [dialogOpen, setDialogOpen] = useState(false)
    const { token, user } = useAuth()
    // Paid access follows the workspace, not the viewer: the backend serves
    // members of a paid workspace, whose own plan is always "free" (AITS-540).
    const { hasPaidAccess, activeWorkspace, loading: workspaceLoading } = useWorkspace()
    const isFree = !hasPaidAccess
    // Formal names of signals that actually have a Workbench fix (queue = to_improve
    // + a curated accelerator). Gates the signal drawer's "Start in Workbench" CTA —
    // this is the same query the sidebar badge runs, so it's usually already cached.
    const { data: workbenchQueue } = useWorkbenchQueue(projectId ? Number(projectId) : undefined)
    const workbenchFixNames = useMemo(
        () => new Set((workbenchQueue?.fixes ?? []).map((f) => f.tech)),
        [workbenchQueue],
    )
    // Selector: report-progress polling is app-wide since AITS-540, so a bare
    // subscription would re-render the whole dashboard every 5s while any
    // report in the workspace builds.
    const setGenerateDialogRequest = useReportsStore((s) => s.setGenerateDialogRequest)
    const requestProgressView = useReportsStore((s) => s.requestProgressView)
    const awaitedReportId = useReportsStore((s) => s.awaitedReportId)
    const awaitedClaimedAt = useReportsStore((s) => s.awaitedClaimedAt)
    const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false)
    const [eligibleDialogOpen, setEligibleDialogOpen] = useState(false)
    const [reRunModalOpen, setReRunModalOpen] = useState(false)
    // The company has reports but not one we can render yet — every one is still
    // generating or errored. Distinguishes "nothing to show" from "still
    // fetching", which otherwise both looked like a spinner that never resolved
    // (AITS-540; the `!report` guard below has no other way to tell them apart).
    const [noViewableReport, setNoViewableReport] = useState(false)
    const eligiblePromptShownRef = useRef(false)
    const lastTrackedViewedReportIdRef = useRef<number | null>(null)
    /** Report id the progress dialog auto-opened for on this visit (see below). */
    const autoOpenedForRef = useRef<number | null>(null)

    const lastLoadedIdRef = useRef<number | null>(null)
    /** Status the loaded report had when its detail was fetched. */
    const loadedStatusRef = useRef<ReportStatus | null>(null)
    const abortRef = useRef<AbortController | null>(null)
    const tokenRef = useRef(token)
    useEffect(() => { tokenRef.current = token }, [token])

    /**
     * This company's reports — the source of the period selector, of which
     * report the dashboard renders, and of the "still building" states.
     *
     * A query rather than the one-shot fetch it used to be. That fetch ran once
     * per company and nothing ever refreshed it, so the list went stale the
     * moment a report finished: the progress dialog navigates to the finished
     * report, the resolver below looked it up in a list that predated it, and
     * sent the user back to the report the list still believed was newest. The
     * score only appeared after a full reload, which is the only thing that
     * refetched the list. As a query it also picks up the `["reports"]`
     * invalidations that creating and re-running a report already fire.
     *
     * Keyed by company, so a company switch can't resolve one company's month
     * against another's reports — what the old stamp-and-compare guard did.
     */
    const {
        data: companyReports,
        isError: loadFailed,
        isFetching: companyReportsFetching,
        fetchStatus: companyReportsFetchStatus,
        refetch: refetchCompanyReports,
    } = useQuery({
        queryKey: ["reports", activeWorkspace?.id, projectId],
        // Closes over `token` rather than the page's `tokenRef`, which is
        // assigned in an effect — an interval-driven refetch can fire between a
        // token refresh and that assignment and send the previous one.
        queryFn: async (): Promise<ReportPeriodSource[]> => {
            const { results } = await projectsInstance.getReports(token, projectId)
            return results ?? []
        },
        enabled: !!token && !workspaceLoading && !!activeWorkspace?.id && !!projectId,
        // Only while something is actually building, and only for this company —
        // it stops on its own as soon as the list comes back finished. This is
        // what lets a report that completes while the user sits on the dashboard
        // (progress dialog dismissed, or never opened) appear without a reload.
        refetchInterval: (query) =>
            (query.state.data ?? []).some((r) => isBuildingStatus(r.status))
                ? BUILDING_POLL_MS
                : false,
    })

    /**
     * We have nothing to show and nothing is coming.
     *
     * `isError` alone is not enough: React Query *pauses* a fetch rather than
     * failing it when the browser is offline (`fetchStatus: "paused"`, status
     * stays "pending"), so an offline cold load would otherwise sit on a loading
     * skeleton forever — where the one-shot fetch this replaced rejected and
     * showed the retryable error card. And `isError` alone is also too much
     * once we hold data: see the render guard below.
     */
    const companyReportsUnavailable =
        companyReports === undefined && (loadFailed || companyReportsFetchStatus === "paused")

    // Which calendar month "the current month" is, as a memo dep. The periods
    // carry a row for it, so a tab left open across midnight on the 1st would
    // otherwise keep offering to generate last month's report until the report
    // list happened to change. Derived on the same basis the period labels use
    // (local month, UTC year), so it turns over exactly when they do.
    const now = new Date()
    const currentMonthKey = `${now.getUTCFullYear()}-${now.toLocaleString("default", { month: "long" })}`

    // Empty — not "a company with no reports" — until the list has actually
    // landed. `buildReportPeriods` always yields a current-month row, and every
    // "you have no report for this month" prompt keys off that row, so deriving
    // from a not-yet-loaded list would flash "generate this month's report" over
    // a company that has one.
    const { periods, minStartDate, maxReportDate, defaultPeriod } = useMemo(
        () => (companyReports ? buildReportPeriods(companyReports, new Date()) : EMPTY_PERIODS),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- currentMonthKey stands in for `new Date()`
        [companyReports, currentMonthKey],
    )

    /** Status of the report in the URL according to this list, if it holds it. */
    const urlReportStatus = reportId
        ? (companyReports ?? []).find((r) => String(r.id) === String(reportId))?.status ?? null
        : null

    /**
     * Whether the list is new enough to resolve a period against.
     *
     * False in exactly one window: a report finished and the progress dialog
     * navigated us onto it before this list was refreshed. Resolving a month
     * then maps it back to the report the list still believes is that month's
     * newest finished one — i.e. straight back off the report that just
     * completed, cancelling the load of it that is already in flight.
     *
     * Deliberately "has it finished" and not "is it here": a re-run is in the
     * list from the moment it is created, so mere presence goes true while the
     * list still disagrees about the only thing the resolver reads.
     */
    const listHasFinishedUrlReport = !reportId || urlReportStatus === "completed"
    // Refresh once per report id the list hasn't caught up with. Bounded on
    // purpose: the report may genuinely still be building (or not be this
    // company's at all), and the period selector has to keep working rather
    // than wedge waiting for a list that will never say what we want.
    const refreshedForRef = useRef<string | null>(null)
    useEffect(() => {
        if (!companyReports || listHasFinishedUrlReport) return
        if (refreshedForRef.current === reportId) return
        refreshedForRef.current = reportId ?? null
        void refetchCompanyReports()
    }, [companyReports, listHasFinishedUrlReport, reportId, refetchCompanyReports])
    const listPredatesUrlReport =
        !listHasFinishedUrlReport &&
        (companyReportsFetching || refreshedForRef.current !== reportId)

    /**
     * The URL names a report this company's list has never heard of, even after
     * the bounded refresh above — a stale tab or an old link to a report that no
     * longer exists (a re-run replaces the month's report server-side, and its
     * detail endpoint 404s). Without this the page sat on its loading skeleton
     * forever, polling a report that will never arrive — which is what our
     * tester read as "generation failed" before regenerating again. Resolve
     * silently to the company dashboard (replace: the dead URL is not a place
     * Back should return to); the resolver lands on the newest report, or on
     * the building card if one is on its way.
     *
     * Deliberately gated the same way as `listPredatesUrlReport`: a report that
     * finished seconds ago can be newer than a cached list, and must get its
     * refresh before we conclude it doesn't exist.
     */
    const urlReportUnknown =
        !!reportId &&
        !!companyReports &&
        !companyReports.some((r) => String(r.id) === String(reportId)) &&
        refreshedForRef.current === reportId &&
        !companyReportsFetching &&
        // A failed refetch keeps stale data with isFetching false — that's a
        // list that couldn't answer, not one that answered "no such report".
        !loadFailed
    useEffect(() => {
        if (!urlReportUnknown) return
        navigate(`/reports/${projectId}`, { replace: true })
    }, [urlReportUnknown, navigate, projectId])

    useEffect(() => {
        if (!report || eligiblePromptShownRef.current) return
        if (user && !user.should_be_greeted && report.can_be_upgraded) {
            setEligibleDialogOpen(true)
            eligiblePromptShownRef.current = true
        }
    }, [report, user])

    // PostHog: emit once per "entering report" (change of report.id), not on refetches.
    useEffect(() => {
        const id = report?.id
        if (!id) return
        if (lastTrackedViewedReportIdRef.current === id) return
        lastTrackedViewedReportIdRef.current = id

        const overallScore = typeof report.overall_score === "string"
            ? parseFloat(report.overall_score)
            : Number(report.overall_score)

        trackReportViewed({
            report_id: id,
            company_id: report.company_id,
            company_name: report.company_name,
            overall_score: overallScore,
        })
    }, [report?.id])

    const loadReportOnce = async (id: number, opts?: { updateState?: boolean; force?: boolean }) => {
        if (!opts?.force && lastLoadedIdRef.current === id) return
        lastLoadedIdRef.current = id
        abortRef.current?.abort()
        const ac = new AbortController()
        abortRef.current = ac
        try {
            const r = await projectsInstance.getReportById(id, tokenRef.current)
            if (ac.signal.aborted) return
            setReport(r)
            loadedStatusRef.current = r.status ?? null

            if (opts?.updateState) {
                const date = new Date(normalizeDateString(r.created_ts))
                // A report still building has no `created_ts` in the detail
                // payload, and "Invalid Date"/"NaN" as the selected period
                // empties the picker until something else heals it.
                if (!Number.isNaN(date.getTime())) {
                    setMonth(date.toLocaleString("default", { month: "long" }))
                    setYear(date.getUTCFullYear().toString())
                }
            }

        } catch (error) {
            // Nothing below concerns a load we ourselves superseded — including
            // the redirect: this runs on a page that may already be unmounted.
            if (ac.signal.aborted) return
            // Let the next attempt through. The id is claimed *before* the
            // request, so leaving it set after a failure means a single
            // transient 5xx pins the page on its loading state with no retry.
            lastLoadedIdRef.current = null
            loadedStatusRef.current = null
            if (
                axios.isAxiosError(error) &&
                error.response?.status === 402 &&
                error.response?.data?.code === "billing_onboarding_required"
            ) {
                navigate("/onboarding", { replace: true })
                return
            }
            setReport(null)
        }
    }

    // Company switch: drop the selected period *during render*, not in an effect.
    //
    // The resolver below runs in the same commit as the switch, and an effect
    // cannot clear the month before it does — so it would resolve the previous
    // company's month against the new company's reports and navigate to
    // whatever that month happens to hold there. (The list can no longer be
    // crossed — it is keyed by company — but the selection is page state, and
    // this is what the old "which company do these periods describe" stamp was
    // really protecting.) React re-runs the render with the reset applied before
    // committing anything, so effects only ever see the cleared value.
    //
    // A company with no report still opens its own dashboard: that is where the
    // "generate this month's report" prompt lives, with the company and
    // workspace already filled in.
    const [selectionProjectId, setSelectionProjectId] = useState(projectId)
    if (selectionProjectId !== projectId) {
        setSelectionProjectId(projectId)
        setMonth("")
        setYear("")
        setReport(null)
        setNoViewableReport(false)
        // A company switch is a fresh visit: its in-flight run (if any) should
        // announce itself again, even one this ref already opened for earlier.
        autoOpenedForRef.current = null
    }

    useEffect(() => {
        if (!projectId) {
            navigate("/")
            return
        }
        // Company-scoped only. `refreshedForRef` is deliberately NOT reset here:
        // it holds a report id, which is unique across companies, and this
        // effect is declared *after* the one that sets it — so on a mount that
        // already has cached reports, clearing it here would undo that refresh
        // and leave the resolver blocked with nothing scheduled to unblock it.
        lastLoadedIdRef.current = null
        loadedStatusRef.current = null
        // Abandon a detail fetch for the company we just left: it would
        // otherwise land and render one company's report under another's name.
        abortRef.current?.abort()
    }, [projectId, navigate])

    // Nothing in flight should be able to set state on an unmounted page.
    useEffect(() => () => abortRef.current?.abort(), [])

    useEffect(() => {
        if (!reportId) return
        // Re-fetch the detail when the list tells us this report finished after
        // we loaded it: `GET /reports/:id` answers 200 mid-build, so what we
        // hold in that case has no scores in it. Only then — a plain revisit is
        // still served from what is already on screen.
        const detailPredatesCompletion =
            lastLoadedIdRef.current === Number(reportId) &&
            loadedStatusRef.current !== "completed" &&
            urlReportStatus === "completed"
        void loadReportOnce(Number(reportId), {
            updateState: true,
            force: detailPredatesCompletion,
        })
    }, [reportId, urlReportStatus])

    // Default the selector to the newest month with something renderable, not to
    // the current-month placeholder — that made the dropdown read "July" while
    // the only thing on screen was June's report. Only when the URL names no
    // report; otherwise that report's own month wins (set by its loader above).
    useEffect(() => {
        if (reportId || month || year || !defaultPeriod) return
        setMonth(defaultPeriod.month)
        setYear(defaultPeriod.year)
    }, [defaultPeriod, month, reportId, year])

    // Resolve the selected period to a report: navigate to it, or — for a month
    // with nothing renderable — keep the month selected and load the newest
    // report we can as background behind its "generate"/"generating" prompt.
    useEffect(() => {
        if (!month || !year) return
        // The list has never seen the report in the URL, so it is older than the
        // page: resolving now would map the month back to the report the list
        // still believes is newest and navigate off the one that just finished.
        if (listPredatesUrlReport) return
        // The selected period still describes the PREVIOUS report — the one in
        // the URL hasn't loaded yet, and only its loader sets the month. That
        // happens whenever something outside this page moves the URL to a report
        // in another month: the shell taking the user to the report they just
        // generated, most of all. Resolving now maps the old month back to the
        // report it holds and navigates straight off the new one.
        if (reportId && String(report?.id) !== String(reportId)) return

        const meta = periods.find((p) => p.month === month && p.year === year)
        if (!meta) return

        if (meta.viewableId !== null) {
            // Something is renderable, so whatever we concluded earlier about
            // there being nothing to show no longer holds. Clearing it here
            // stops the "still building" card flashing over the loading state
            // while the newly-selected report is fetched.
            setNoViewableReport(false)
            // Navigation needed if this isn't already the report in the URL.
            // With no reportId at all, String(undefined) !== String(123), so we
            // navigate — which is how a bare /reports/:projectId gets a report.
            //
            // That case *replaces*: `/reports/:projectId` is a resolver, not a
            // destination, so pushing would make Back land on it and resolve
            // straight forward again — trapping the user on the dashboard. A
            // month the user actually picked is a real destination and pushes.
            if (String(meta.viewableId) !== String(reportId)) {
                // The awaited report inside its pacing window is the shell's to
                // reveal, on the dialog's beat — resolving onto it now would
                // swap the page behind the progress dialog mid-"run" and spoil
                // the reveal. The shell navigates here itself once the dwell
                // has passed (`useAwaitedReport`), so skipping is safe.
                const dwelling =
                    awaitedReportId != null &&
                    String(meta.viewableId) === String(awaitedReportId) &&
                    awaitedClaimedAt != null &&
                    Date.now() - awaitedClaimedAt < MIN_GENERATION_DWELL_MS
                if (!dwelling) {
                    navigate(`/reports/${projectId}/${meta.viewableId}`, { replace: !reportId })
                }
            }
            return
        }

        // Selected month has nothing to render — no report yet, or its only ones
        // are still building or errored. Deliberately NOT the month's newest
        // report: `GET /reports/:id` answers 200 for a report that is still
        // building, with no scores in it, so navigating there would swap the
        // dashboard for a zero-score one mid-build.
        const fallbackId = periods.find((p) => p.viewableId !== null)?.viewableId
        if (fallbackId == null) {
            // Nothing this company has is renderable — every report is still
            // generating or errored. Say so; returning silently left the
            // `!report` spinner up forever (AITS-540).
            setNoViewableReport(true)
            return
        }

        setNoViewableReport(false)
        loadReportOnce(fallbackId, { updateState: false })
    }, [month, year, periods, listPredatesUrlReport, report?.id, reportId, projectId, navigate, awaitedReportId, awaitedClaimedAt])

    // A report is in flight and there is nothing finished to show instead.
    // Deliberately NOT gated on `noViewableReport`: that flag is only set on the
    // placeholder branch, and the completed-first default above means we rarely
    // land there — which silently disabled the auto-opening progress dialog.
    const buildingReportId = useMemo(() => buildingReportIdOf(periods), [periods])
    // ...and the broader "this company has a run in flight at all", which is
    // what announces itself below whether or not an older report renders.
    const companyBuildingReportId = useMemo(() => anyBuildingReportIdOf(periods), [periods])

    // A run in flight for the company on screen opens the live progress dialog
    // by itself — on every visit to this dashboard (refresh, or navigating away
    // and back), or the user has no way of understanding what's going on.
    // Once per report id per mount, so dismissing it sticks until the next
    // visit rather than re-opening on the next poll tick.
    //
    // View-only on purpose: watching is not owning. Only the tab that started
    // a run (create / re-run) claims it and gets routed on completion — a
    // workspace member who merely has this dashboard open sees this same
    // dialog, can dismiss it, and gets the completion toast instead.
    useEffect(() => {
        if (companyBuildingReportId == null) return
        if (autoOpenedForRef.current === companyBuildingReportId) return
        autoOpenedForRef.current = companyBuildingReportId
        requestProgressView(companyBuildingReportId)
    }, [companyBuildingReportId, requestProgressView])

    const currentPeriod = periods.find((p) => p.month === month && p.year === year)

    // The current month has no report yet — the period list carries a row with
    // no report for it. Drives the "generate this month" prompt, which is
    // independent of which period you happen to be looking at.
    // Also true when the company has reports but none of them ever completed —
    // e.g. every attempt errored. Without this the page rendered a header and
    // nothing else: no report to show, and no prompt to run another.
    const hasAnyCompletedReport = periods.some((p) => p.viewableId !== null)
    // A failed run leaves the current month with a report that is neither
    // renderable nor building, so the month read as "has a report" and the
    // prompt stayed away — after a failed regeneration that meant no retry
    // path anywhere on the dashboard. Offering to generate again is the whole
    // recovery, and deliberately all it does. Scoped to the current month: an
    // old month whose only report errored is history, not a prompt.
    const currentMonthRow = periods.find(
        (p) => `${p.year}-${p.month}` === currentMonthKey,
    )
    const currentMonthFailed =
        !!currentMonthRow &&
        currentMonthRow.latestStatus === "error" &&
        currentMonthRow.viewableId === null
    const currentMonthNeedsReport =
        periods.some((p) => p.latestStatus === null) ||
        currentMonthFailed ||
        (periods.length > 0 && !hasAnyCompletedReport)
    const currentMonthLabel =
        periods.find((p) => p.latestStatus === null)?.month ??
        new Date().toLocaleString("default", { month: "long" })

    // A build is in flight for the month on screen. Independent of what the month
    // renders: a re-run shows this banner above the previous report for the same
    // month, which is still the newest finished one until the re-run lands.
    const isGeneratingPeriod = !!currentPeriod && isBuildingStatus(currentPeriod.latestStatus)

    const generatingReportId = isGeneratingPeriod ? currentPeriod.latestId : null

    // Entry point into the MarcusAI Workbench (MW-4), which replaced the retired
    // linear /recommendations flow. The workbench is company-scoped.
    // `openTopFix` is an act-intent: on mobile the workbench opens straight to the
    // top fix's detail rather than the queue list (AITS-602 #3). Desktop ignores
    // it — the split panel shows the detail beside the list regardless.
    const handleSeeMore = () =>
        navigate(`/reports/${projectId}/workbench`, { state: { openTopFix: true } })

    // Through the store, not page-local state: the shell owns the progress
    // dialog, so there is exactly one dialog mount. View-only — watching is
    // not owning (see the auto-open effect above).
    const handleCheckGeneratingStatus = () => {
        if (generatingReportId) {
            requestProgressView(generatingReportId)
        }
    }

    // Compute whether the loaded report was created in the current calendar month
    const isCurrentMonthReport = (() => {
        if (!report?.created_ts) return false
        const normalized = normalizeDateString(report.created_ts)
        const reportDate = new Date(normalized)
        const now = new Date()
        return (
            reportDate.getMonth() === now.getMonth() &&
            reportDate.getFullYear() === now.getFullYear()
        )
    })()

    const handleReRunStarted = (newReportId: number) => {
        // Stay on the report; the progress dialog opens over it. The old detour
        // to Home was only there because Home was the sole mounting point for
        // the polling hook that fills the dialog's store — it lives in the shell
        // now (AITS-540).
        setGenerateDialogRequest(newReportId)
    }

    // Calculate signals count to determine if it's free report or legacy-free
    const signalsCount = report?.signals ? Object.values(report.signals).flat().length : 0
    // Free report: is_paid = false AND signals.length <= 9
    // Legacy-free report: is_paid = false AND signals.length > 10
    const isFreeReport = !report?.is_paid && signalsCount <= 9
    const isLegacyFree = !report?.is_paid && signalsCount > 10

    // Only a *cold* failure takes over the page. React Query keeps the last good
    // data through a failed refetch, and this query now refetches in the
    // background — on focus, and every 5s while a report builds. Keyed on the
    // error alone, one transient 5xx mid-poll would replace a fully rendered
    // report with this error screen.
    if (companyReportsUnavailable) {
        return (
            <div className="vb-page">
                <VbError
                    title="Couldn't load this company's reports"
                    description="The request failed. Check your connection and try again."
                    onRetry={() => void refetchCompanyReports()}
                />
            </div>
        )
    }

    // Only a build in flight short-circuits the page — the progress dialog is
    // already open over this (see the effect above), and this is what you get
    // back to if you dismiss it. A company with NO report renders the normal
    // page instead, so its "generate a report" prompt stays reachable.
    //
    // The copy names the month and sets a duration on purpose: this card is all
    // a regenerating user sees for ~7 minutes (update-framing — it never says
    // the month's previous report is gone), and the generic version read as a
    // failure, which is what sent our tester back to the regenerate button.
    if (buildingReportId) {
        const buildingMonth = periods.find((p) => p.latestId === buildingReportId)?.month
        return (
            <div className="vb-page">
                <VbEmpty
                    title={
                        buildingMonth
                            ? `Your ${buildingMonth} report is on its way`
                            : "Your report is on its way"
                    }
                    description="We're checking this company against every AI Trust Signal. A full run usually takes about 7 minutes, and this page fills in the moment it's ready — you don't need to do anything."
                    action={
                        <VbButton onClick={() => requestProgressView(buildingReportId)}>
                            Show progress
                        </VbButton>
                    }
                />
            </div>
        )
    }

    if (!report && !noViewableReport) {
        return (
            <div className="vb-page">
                <VbLoading rows={2} />
            </div>
        )
    }
    
    // For placeholder period, we need company info, which usually comes from report.
    // If no report loaded (new project), we might need to rely on what we have.
    // With no report at all there is nothing to read a name off, so fall back to
    // the workspace's own company record.
    const workspaceCompany = activeWorkspace?.companies?.find(
        (c) => c.company_id === Number(projectId),
    )
    const displayCompanyName =
        report?.company_name ||
        workspaceCompany?.company_name ||
        report?.company_url ||
        "This company";

    return (
        <div className="vb-page">
            {/* Portalled dialogs — mounted once, independent of layout. */}
            <EligibleReportDialog
                open={eligibleDialogOpen}
                onOpenChange={setEligibleDialogOpen}
                companyName={displayCompanyName}
                reportId={report?.id}
            />
            {/* Keep dev's removal of the Marcus AI report bubble (AITS-586).
                The null guard is ours: this page now renders for a company
                with no report at all, so `report.id` can't be read eagerly. */}
            {report ? (
                <ReRunReportModal
                    open={reRunModalOpen}
                    onOpenChange={setReRunModalOpen}
                    reportId={report.id}
                    companyId={projectId ? Number(projectId) : undefined}
                    onReRunStarted={handleReRunStarted}
                />
            ) : null}
            <ScheduleFormDialog
                open={scheduleDialogOpen}
                onOpenChange={setScheduleDialogOpen}
                mode="create"
                companyOptions={[{ value: Number(projectId), label: displayCompanyName }]}
                lockedCompany={{ value: Number(projectId), label: displayCompanyName }}
                onSave={async ({ recipients, generationDay }) => {
                    await projectsInstance.createScheduledReport(
                        [Number(projectId)],
                        recipients,
                        generationDay,
                        token
                    )
                }}
            />
            {/* Page header — the company's identity (favicon + name) as the title,
                with the report-scoped actions (period / share / re-run / schedule)
                right-aligned. Hand-rolls the unscoped `.vb-page-hd` primitive so the
                favicon can carry an inline adornment (mirrors BuyerDetailPage). A slim
                trial band, when it lands (separate onboarding epic), sits above. */}
            <header className="vb-page-hd">
                <div className="vb-page-hd__left">
                    <h1 className="vb-page-hd__title">
                        {/* `.vb-fav` is `.vb-root`-scoped; the report shell isn't, so
                            wrap just the favicon in a tiny vb-root island to style it. */}
                        <span className="vb-root inline-flex items-center">
                            <CompetitorFavicon
                                name={displayCompanyName}
                                url={report?.company_url ?? workspaceCompany?.company_url ?? ""}
                                size={26}
                            />
                        </span>
                        {displayCompanyName}
                    </h1>
                </div>
                <div className="vb-page-hd__actions">
                    <SignalsActions
                        periods={periods}
                        selectedMonth={month}
                        selectedYear={year}
                        onMonthChange={setMonth}
                        onYearChange={setYear}
                        reportId={report?.id}
                        companyId={report?.company_id}
                        companyName={report?.company_name}
                        isFreeUser={isFree}
                        showReRunButton={isCurrentMonthReport && !isGeneratingPeriod}
                        onReRun={() => setReRunModalOpen(true)}
                        showScheduleButton={!isFree}
                        onSchedule={() => setScheduleDialogOpen(true)}
                    />
                </div>
            </header>

            {/* Placeholder / generating months show their call to action ABOVE the
                dashboard rather than instead of it. Swapping them meant landing on
                a company early in the month showed only a "generate" prompt — no
                score, no signals — with the last report reachable only by knowing
                to change the month dropdown. */}
            {isGeneratingPeriod ? (
                <div className="w-full bg-vb-brand-soft border rounded p-16 flex flex-col justify-center items-center gap-4">
                    <h3 className="text-xl font-semibold text-center">
                        Your AI Trust Signals report for {month} is being generated
                    </h3>
                    <p className="text-center text-gray-600 mb-4">
                        We're analyzing your company's AI Trust Signals across the web. This usually takes a few minutes.
                    </p>
                    <Button
                        onClick={handleCheckGeneratingStatus}
                        variant="default"
                        className="bg-primary text-white hover:bg-primary/90"
                    >
                        Check Generation Status
                    </Button>
                </div>
            ) : currentMonthNeedsReport ? (
                <div className="w-full bg-vb-brand-soft border rounded p-16 flex flex-col justify-center items-center gap-4">
                    <h3 className="text-xl font-semibold text-center">
                        Generate your AI Trust Signals report for {currentMonthLabel}
                    </h3>
                    <div className="relative group">
                        {(user?.total_credits || user?.credits || 0) <= 0 ? (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span tabIndex={0} className="inline-block cursor-not-allowed">
                                            <Button
                                                onClick={() => setDialogOpen(true)}
                                                disabled
                                                className="pointer-events-none"
                                            >
                                                Check My AI Trust Signals
                                            </Button>
                                        </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>Your credit balance is 0</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        ) : (
                            <Button 
                                onClick={() => setDialogOpen(true)}
                            >
                                Check My AI Trust Signals
                            </Button>
                        )}
                    </div>
                    <ReportDialog
                        open={dialogOpen}
                        setOpen={setDialogOpen}
                        initialCompanyUrl={report?.company_url ?? workspaceCompany?.company_url ?? undefined}
                        initialCompanyName={displayCompanyName}
                        onClick={(_, newId) => { if (newId) setGenerateDialogRequest(newId); }}
                    />
                </div>
            ) : null}

            {/* The report itself. On a placeholder/generating month this is the
                newest completed one, loaded as background above. */}
            {report ? (
            <div className="space-y-8">
                {/* Hero — the AI Trust Signal Score card (left) + the next thing to
                    do (right, dominant ~1:1.35). Collapses to one column below xl,
                    score first. The score card folds in the Marcus summary and a
                    "Trend" tab (AITS-491), so the old standalone Marcus / progress
                    band is gone. */}
                <div className="grid grid-cols-1 items-stretch gap-3.5 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)]">
                    <ScoreCard
                        report={report}
                        isFree={isFree}
                        companyId={projectId ? Number(projectId) : undefined}
                        token={token}
                        minStartDate={minStartDate ?? undefined}
                        maxReportDate={maxReportDate ?? undefined}
                        onUnlock={() => navigate("/settings?tab=billing")}
                    />
                    <NextThingCard
                        report={report}
                        companyId={projectId ? Number(projectId) : undefined}
                        onStart={handleSeeMore}
                        isFree={isFree}
                        onUnlock={() => navigate("/settings?tab=billing")}
                    />
                </div>

                {/* Section heading introduces the tiered signals. */}
                <div className="space-y-4">
                    <h2 className="text-[19px] font-bold leading-tight tracking-[-0.014em] text-vb-text">
                        Here's how you stand.
                    </h2>
                    <SignalCards
                        signals={report.signals}
                        isLegacyReportFree={isLegacyFree}
                        isFreeReport={isFreeReport}
                        isUserSubscribed={!isFree}
                        canBeUpgraded={report.can_be_upgraded}
                        reportId={report.id}
                        workbenchFixNames={workbenchFixNames}
                        onStartWorkbench={() =>
                            navigate(`/reports/${projectId}/workbench`, { state: { openTopFix: true } })
                        }
                    />
                </div>

                {/* Reputation readout closes the dashboard. */}
                <ReputationCard
                    reportId={report.id}
                    projectId={projectId!}
                />
            </div>
            ) : null}
        </div>
    )
}
